<template>
  <div ref="mainCard" style="width: 100%">
    <v-card style="width: 100%" :disabled="!isDeviceConnected" tile>
      <v-progress-linear
        :indeterminate="!isDeviceConnected"
        color="rgb(200, 200, 200)"
      />
      <v-card-text>
        <div style="width: 100%">
          <RadialGauge :value="displayValue" :options="options" />
        </div>
      </v-card-text>
      <v-card-actions>
        <v-select
          v-model="selectUnit"
          label="Unit"
          :items="props.settings.unit_items"
          variant="underlined"
        >
        </v-select>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import RadialGauge from "./RadialGauge.vue";
import { PortState } from "@/enums/PortState";
import { useModbusUtils } from "@/composables/useModbusUtils";
import { useModbusAddress } from "@/composables/useModbusAddress";
import { useModbusStore } from "@/stores/useModbusStore";
import { DashboardItem } from "@/types/DashboardItem";
import { DashboardRange } from "@/types/DashboardRange";
import { ModbusParam } from "@/types/ModbusParam";
import { RadialGaugeOptions } from "@/types/RadialGaugeOptions";
import { TimerId } from "@/types/TimerId";

const { getModbusDataLen } = useModbusUtils();
const modbusStore = useModbusStore();
const { modbusAddress } = useModbusAddress();

const props = defineProps<{
  settings: DashboardItem;
  parameter: ModbusParam;
}>();

const baseUnit = ref(props.settings.unit_items[0]);
const selectUnit = ref(props.settings.unit_items[0]);

const modbusValue = ref<number>(props.settings.valueRange.min);
const displayValue = ref<number>(modbusValue.value);
const modbusReqTimer = ref<TimerId>(0);

const options = ref<RadialGaugeOptions>({
  width: 200,
  height: 200,
  minValue: props.settings.valueRange.min,
  maxValue: props.settings.valueRange.max,
  animationDuration: 1500,
  animationRule: "elastic",
  majorTicks: props.settings.ticks,
  units: props.settings.unit,
  title: props.settings.name,
  highlights: props.settings.ranges,
  colorBar: "white",
  colorBarProgress: "blue",
  borders: false,
  needleWidth: 3,
  needleCircleSize: 7,
  barWidth: 2,
  valueDec: 2,
});

const isDeviceConnected = computed(() => {
  return modbusStore.portState === PortState.connected;
});

async function updateOptions() {
  const this_unit = modbusStore.guiUnits.find(
    (u) => u.name == selectUnit.value,
  );
  options.value = {
    ...options.value,
    minValue: modbusStore.convert(
      props.settings.valueRange.min,
      baseUnit.value,
      selectUnit.value,
    ),
    maxValue: modbusStore.convert(
      props.settings.valueRange.max,
      baseUnit.value,
      selectUnit.value,
    ),
    majorTicks: props.settings.ticks.map((r: number): string => {
      const rc = modbusStore.convert(r, baseUnit.value, selectUnit.value);
      return rc < 1000
        ? rc.toFixed(0).toString()
        : rc % 1000 == 0
          ? (rc / 1000).toString() + "k"
          : (rc / 1000).toFixed(1).toString() + "k";
    }),
    units: this_unit ? this_unit.Symbol : "",
    title: props.settings.name,
    highlights: props.settings.ranges.map((range: DashboardRange) => ({
      color: range.color,
      from: modbusStore.convert(range.from, baseUnit.value, selectUnit.value),
      to: modbusStore.convert(range.to, baseUnit.value, selectUnit.value),
    })),
  };
}

watch(selectUnit, updateOptions);

onMounted(() => {
  modbusReqTimer.value = setInterval(async () => {
    if (
      modbusStore.storeInit &&
      modbusStore.portState === PortState.connected
    ) {
      modbusStore.modbusRead(
        modbusStore.deviceAddress,
        modbusAddress[props.settings.modbusAddressName],
        getModbusDataLen(props.parameter.type),
      );
      const getShowValueResult: number | boolean | undefined | null =
        modbusStore.getShowValue(
          modbusAddress[props.settings.modbusAddressName],
          props.parameter.type,
        );
      modbusValue.value =
        typeof getShowValueResult === "number"
          ? getShowValueResult
          : props.settings.valueRange.min;
      displayValue.value = modbusStore.convert(
        modbusValue.value,
        props.settings.unit_items[0],
        selectUnit.value,
      );
    } else {
      modbusValue.value = props.settings.valueRange.min;
    }
  }, 600);
});

onUnmounted(() => {
  clearInterval(modbusReqTimer.value);
});
</script>
